/**
 * @file Layer-loader.ts
 * @module LayerLoader
 * @description Kartes slāņu konfigurācijas ielādētājs marķieru mini kartei.
 *              Veic API pieprasījumu uz /layer/layers galapunktu, lai iegūtu Pamatkarte
 *              un datu slāņu definīcijas. Apstrādā MVT stila konfigurāciju, apvienoto
 *              flīžu (merged tiles) URL un stilu repozitorija atjaunināšanu.
 *              Rezultāts tiek kešots (singleton Promise), lai izvairītos no atkārtotiem pieprasījumiem.
 */
import axios from "axios";
import { AltLayerEntity, DataResponse, GroupEntity, LayerResponse, setLayerEntities } from "../../store/Layers.repository";
import { ENTRYPOINT } from "../../map/Endpoints";
import { MERGED_TILE_URL_LIST } from "../../data-layers/Data-layers-new-layer-manager";
import { getMVTStyles } from "../../data-layers/Mapbox-helper";
import { updateStyleItems } from "../../data-layers/Style-layer.repository";

interface MainLayerResponse {
	base: LayerResponse[];
	data: DataResponse[];
}

let layerPromise: Promise<MainLayerResponse> | null = null;

export const getLayerDef = () => {
	if (!layerPromise) {
		layerPromise = axios.get<{ base: LayerResponse[]; data: DataResponse[] }>(ENTRYPOINT + "/layer/layers").then((res) => {
			return res.data;
		});
	}
	return layerPromise.then((res) => {
		const entities = res.data.map(
			(d) =>
				({
					attributions: d.attributions,
					name: d.name,
					id: "data-" + d.id,
					group: "data",
					dataSource: d.url_to_datasource,
					use_uvis_popups: false,
					layers: (d as DataResponse).layers.map((l) => ({
						...l,
						group: "data",
						groupName: d.name,
						merged_tiles: (l as any).merged_tiles,
						zoom_from: (l as any).zoom_from,
					})),
				} as GroupEntity)
		);
		const styleLayers = entities.flatMap((g) => {
			const layers = g.layers as any;
			return {
				id: "data-" + g.id,
				name: g.name,
				layers: layers.map((l: AltLayerEntity, i: number) => {
					if (l.merged_tiles) {
						if (!MERGED_TILE_URL_LIST.includes(l.merged_tiles)) {
							MERGED_TILE_URL_LIST.push(l.merged_tiles);
						}
						l.original_table = l.table;
						l.table = "merged_tiles_" + MERGED_TILE_URL_LIST.indexOf(l.merged_tiles);
					}
					const [lines, points] = getMVTStyles([l], !!l.merged_tiles);

					layers[i].points = points; //For clusters
					return {
						...l,
						name: l.name,
						id: l.name + "-" + l.id,
						visible: true,
						table: l.table,
						originalTable: l.original_table,
						lines: lines,
						points: points,
						clustered: l.clustered,
						attributions: l.attributions,
						index: i,
						mvt: true,
						records: l.records_exist,
					};
				}),
				visible: true,
				dataSource: g.dataSource,
			};
		});
		updateStyleItems(styleLayers);

		return res;
	});
};
